import {
  EventEmitter,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { fromEvent, Subscription } from 'rxjs';
import { DropContextMenuComponent } from '../context-menus/drop-context-menu/drop-context-menu.component';
import { ShelfContextMenuComponent } from '../context-menus/shelf-context-menu/shelf-context-menu.component';
import { ProductContextMenuComponent } from '../context-menus/product-context-menu/product-context-menu.component';
import { ProductBinContextMenuComponent } from '../context-menus/product-bin-context-menu/product-bin-context-menu.component';
import { LayoutService } from './layout.service';
import { ProductBinService } from './productbin.service';
import { DropSettingsModalComponent } from '../modals/drop-settings-modal/drop-settings-modal.component';
import { DialogService } from './dialog.service';
import { ModalService } from './modal.service';
import { ValidationError } from './planogram-validation.service';
import { ClipboardService } from './clipboard.service';
import { ProductOrientation } from '../models/layout/product-orientation';
import { ToastService } from './toasts.service';
import { DropSettings } from '../models/drop-settings';

export interface DropContextMenuParams {
  position: { x: number; y: number };
  dropIndex: number;
}
export const DROP_CONTEXT_MENU_PARAMS =
  new InjectionToken<DropContextMenuParams>('DROP_CONTEXT_MENU_PARAMS');

export interface ShelfContextMenuParams {
  position: { x: number; y: number };
  dropIndex: number;
  shelfIndex: number;
}
export const SHELF_CONTEXT_MENU_PARAMS =
  new InjectionToken<ShelfContextMenuParams>('SHELF_CONTEXT_MENU_PARAMS');

export interface ProductContextMenuParams {
  position: { x: number; y: number };
  dropIndex: number;
  shelfIndex: number;
  stackIndex: number;
  productIndex: number;
}
export const PRODUCT_CONTEXT_MENU_PARAMS =
  new InjectionToken<ProductContextMenuParams>('PRODUCT_CONTEXT_MENU_PARAMS');

export interface ProductBinContextMenuParams {
  position: { x: number; y: number };
  productId: number;
}
export const PRODUCT_BIN_CONTEXT_MENU_PARAMS =
  new InjectionToken<ProductBinContextMenuParams>('PRODUCT_BIN_CONTEXT_MENU_PARAMS');

@Injectable({
  providedIn: 'root',
})
export class ContextMenuService {
  private overlayRef: OverlayRef | null = null;
  private mouseMoveSub?: Subscription;
  private clickSub?: Subscription;

  constructor(
    private overlay: Overlay,
    private dialogService: DialogService,
    private modalService: ModalService,
    private layoutService: LayoutService,
    private clipboardService: ClipboardService,
    private toastService: ToastService,
    private productBinService: ProductBinService,
    private injector: Injector
  ) {}

  openDropContextMenu(event: MouseEvent, dropIndex: number): EventEmitter<string> {
    return this.openMenu(
      event,
      DropContextMenuComponent,
      DROP_CONTEXT_MENU_PARAMS,
      { position: { x: event.clientX, y: event.clientY }, dropIndex }
    );
  }

  openShelfContextMenu(
    event: MouseEvent,
    dropIndex: number,
    shelfIndex: number
  ): EventEmitter<string> {
    return this.openMenu(
      event,
      ShelfContextMenuComponent,
      SHELF_CONTEXT_MENU_PARAMS,
      { position: { x: event.clientX, y: event.clientY }, dropIndex, shelfIndex }
    );
  }

  openProductContextMenu(
    event: MouseEvent,
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ): EventEmitter<string> {
    return this.openMenu(
      event,
      ProductContextMenuComponent,
      PRODUCT_CONTEXT_MENU_PARAMS,
      {
        position: { x: event.clientX, y: event.clientY },
        dropIndex,
        shelfIndex,
        stackIndex,
        productIndex,
      }
    );
  }

  openProductBinContextMenu(event: MouseEvent, productId: number): EventEmitter<string> {
    return this.openMenu(
      event,
      ProductBinContextMenuComponent,
      PRODUCT_BIN_CONTEXT_MENU_PARAMS,
      { position: { x: event.clientX, y: event.clientY }, productId }
    );
  }

  closeContextMenu() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    if (this.mouseMoveSub) {
      this.mouseMoveSub.unsubscribe();
      this.mouseMoveSub = undefined;
    }
    if (this.clickSub) {
      this.clickSub.unsubscribe();
      this.clickSub = undefined;
    }
  }

  private openMenu<P>(
    event: MouseEvent,
    component: Type<any>,
    token: InjectionToken<P>,
    params: P
  ): EventEmitter<string> {
    this.closeContextMenu();

    this.overlayRef = this.overlay.create(this.createOverlayConfig(event));

    const emitter = new EventEmitter<string>();
    const injector = Injector.create({
      providers: [
        { provide: token, useValue: params },
        { provide: EventEmitter, useValue: emitter },
      ],
      parent: this.injector,
    });

    const componentRef = this.overlayRef.attach(
      new ComponentPortal(component, null, injector)
    );
    const componentInstance = componentRef.instance;
    const menuElement = componentRef.location.nativeElement;

    this.connectComponentEmitters(componentInstance, emitter, event);
    this.clickSub = this.createMouseClickSub(menuElement);
    this.mouseMoveSub = this.createMouseMoveSub(menuElement);

    this.overlayRef.backdropClick().subscribe(() => this.closeContextMenu());
    this.overlayRef.detachments().subscribe(() => this.closeContextMenu());

    return emitter;
  }

  private createOverlayConfig(event: MouseEvent) {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo({
        x: event.clientX,
        y: event.clientY,
        width: 0,
        height: 0,
      })
      .withPositions([
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
        { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
      ])
      .withViewportMargin(8)
      .withGrowAfterOpen(true)
      .withPush(true);

    return {
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'context-menu-container',
      hasBackdrop: false,
    };
  }

  private runMenuAction(fn: () => void | Promise<void>): void | Promise<void> {
    try {
      const result = fn();
      if (result instanceof Promise) {
        return result.catch((error) => this.reportMenuError(error));
      }
    } catch (error) {
      this.reportMenuError(error);
    }
  }

  private reportMenuError(error: unknown): void {
    if (error instanceof ValidationError) {
      this.dialogService.showError(error.message);
    } else {
      this.dialogService.showError('An unexpected error occurred');
      console.error(error);
    }
  }

  private handleDropMenuAction(action: string, dropIndex: number): void {
    this.runMenuAction(() => {
      switch (action) {
        case 'remove':
          this.layoutService.removeDrop(dropIndex);
          break;
        case 'clear':
          this.layoutService.clearDrop(dropIndex);
          break;
        case 'copy':
          this.layoutService.copyDrop(dropIndex);
          break;
        case 'pasteBefore':
          this.layoutService.pasteDropBefore(dropIndex);
          break;
        case 'pasteAfter':
          this.layoutService.pasteDropAfter(dropIndex);
          break;
        case 'createBefore':
          this.layoutService.addDropBefore(dropIndex);
          break;
        case 'createAfter':
          this.layoutService.addDropAfter(dropIndex);
          break;
        case 'pasteShelf':
          this.layoutService.pasteShelf(dropIndex);
          break;
        case 'createShelf':
          this.layoutService.createShelf(dropIndex);
          break;
        case 'settings':
          this.modalService
            .openModalWithOptions(
              DropSettingsModalComponent,
              { size: 'lg', centered: true },
              { dropIndex }
            )
            .then((result: DropSettings) => {
              if (result) {
                this.layoutService.setDropSettings(dropIndex, result);
              }
            });
          break;
      }
    });
  }

  private handleShelfMenuAction(
    action: string,
    dropIndex: number,
    shelfIndex: number
  ): void {
    this.runMenuAction(() => {
      switch (action) {
        case 'remove':
          this.layoutService.removeShelf(dropIndex, shelfIndex);
          break;
        case 'addAbove':
          this.layoutService.addShelf(dropIndex, shelfIndex);
          break;
        case 'addBelow':
          this.layoutService.addShelf(dropIndex, shelfIndex + 1);
          break;
        case 'copy': {
          const currentLayout = this.layoutService.getCurrentLayout(false);
          const shelf = currentLayout?.drops[dropIndex]?.shelves[shelfIndex];
          if (shelf) {
            this.clipboardService.copyShelf(shelf);
          }
          break;
        }
        case 'pasteAbove':
          this.layoutService.pasteShelfAbove(dropIndex, shelfIndex);
          break;
        case 'pasteBelow':
          this.layoutService.pasteShelfBelow(dropIndex, shelfIndex);
          break;
        case 'clear':
          this.layoutService.clearShelf(dropIndex, shelfIndex);
          break;
      }
    });
  }

  private handleProductMenuAction(
    action: string,
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ): void {
    const orientationMap: Record<string, ProductOrientation> = {
      'changeOrientation:front': ProductOrientation.FRONT,
      'changeOrientation:front90': ProductOrientation.FRONT_90,
      'changeOrientation:side': ProductOrientation.SIDE,
      'changeOrientation:side90': ProductOrientation.SIDE_90,
      'changeOrientation:flatpack': ProductOrientation.FLATPACK,
      'changeOrientation:flatpack90': ProductOrientation.FLATPACK_90,
    };

    this.runMenuAction(() => {
      if (action in orientationMap) {
        this.layoutService.changeProductOrientation(
          dropIndex,
          shelfIndex,
          stackIndex,
          productIndex,
          orientationMap[action]
        );
        return;
      }

      switch (action) {
        case 'openShelfMenu':
          // Handled in connectComponentEmitters
          break;
        case 'copyProduct': {
          const product = this.layoutService.getProduct(dropIndex, shelfIndex, stackIndex, productIndex);
          if (product) {
            this.clipboardService.copyProduct(product);
            this.showCopyToast('Product Copied');
          }
          break;
        }
        case 'copyStack': {
          const stack = this.layoutService.getStack(dropIndex, shelfIndex, stackIndex);
          if (stack) {
            this.clipboardService.copyStack(stack);
            this.showCopyToast('Stack Copied');
          }
          break;
        }
        case 'pasteProductAfter':
          this.layoutService.pasteProductAfter(dropIndex, shelfIndex, stackIndex);
          break;
        case 'pasteProductBefore':
          this.layoutService.pasteProductBefore(dropIndex, shelfIndex, stackIndex);
          break;
        case 'pasteStackBefore':
          this.layoutService.pasteStackBefore(dropIndex, shelfIndex, stackIndex);
          break;
        case 'pasteStackAfter':
          this.layoutService.pasteStackAfter(dropIndex, shelfIndex, stackIndex);
          break;
        case 'increaseStackHeight':
          this.layoutService.addVFacing(dropIndex, shelfIndex, stackIndex, productIndex);
          break;
        case 'decreaseStackHeight':
          this.layoutService.removeVFacing(dropIndex, shelfIndex, stackIndex, productIndex);
          break;
        case 'increaseProductFacings':
          this.layoutService.addHFacing(dropIndex, shelfIndex, stackIndex, productIndex);
          break;
        case 'decreaseProductFacings':
          this.layoutService.removeHFacing(dropIndex, shelfIndex, stackIndex, productIndex);
          break;
        case 'removeProduct':
          this.layoutService.removeProduct(dropIndex, shelfIndex, stackIndex, productIndex);
          break;
        case 'removeStack':
          this.layoutService.removeStack(dropIndex, shelfIndex, stackIndex);
          break;
        case 'clearStackAbove':
          this.layoutService.clearStackAbove(dropIndex, shelfIndex, stackIndex, productIndex);
          break;
        case 'clearStackBelow':
          this.layoutService.clearStackBelow(dropIndex, shelfIndex, stackIndex, productIndex);
          break;
      }
    });
  }

  private handleProductBinMenuAction(action: string, productId: number): void {
    this.runMenuAction(async () => {
      switch (action) {
        case 'removeSingle': {
          const confirmed = await this.dialogService.showConfirmation(
            'Remove product from layout',
            'Are you sure you want to remove this product from the layout? The product will remain in the bin.'
          );
          if (confirmed) {
            this.layoutService.removeProductFromLayout(productId);
          }
          break;
        }
        case 'removeFiltered': {
          const filteredIds = this.productBinService.getCurrentlyFilteredProductIds();
          const breakdown = this.layoutService.getActualFacingCounts();
          const onLayoutIds = filteredIds.filter(id => (breakdown.get(id) ?? 0) > 0);

          if (onLayoutIds.length === 0) {
            this.dialogService.showError('No filtered products are currently on the layout.');
            break;
          }

          const confirmed = await this.dialogService.showConfirmation(
            'Remove filtered products from layout',
            `Are you sure you want to remove ${onLayoutIds.length} product${onLayoutIds.length === 1 ? '' : 's'} from the layout? The products will remain in the bin.`
          );
          if (confirmed) {
            this.layoutService.bulkRemoveProductsFromLayout(onLayoutIds);
          }
          break;
        }
      }
    });
  }

  private showCopyToast(message: string): void {
    this.toastService.show(message, {
      classname: 'bg-dark text-light p-3 fs-5',
      delay: 1000,
    });
  }

  private connectComponentEmitters(
    componentInstance: any,
    emitter: EventEmitter<string>,
    event: MouseEvent
  ) {
    Object.keys(componentInstance)
      .filter((key) => componentInstance[key] instanceof EventEmitter)
      .forEach((key) => {
        componentInstance[key].subscribe((action: string) => {
          if (action === 'openDropMenu') {
            this.closeContextMenu();
            setTimeout(() => {
              const params = componentInstance.params as ShelfContextMenuParams;
              this.openDropContextMenu(event, params.dropIndex);
            }, 0);
          } else if (action === 'openShelfMenu') {
            this.closeContextMenu();
            setTimeout(() => {
              const params = componentInstance.params as ProductContextMenuParams;
              this.openShelfContextMenu(event, params.dropIndex, params.shelfIndex);
            }, 0);
          } else {
            if (componentInstance instanceof DropContextMenuComponent) {
              this.handleDropMenuAction(action, componentInstance.params.dropIndex);
            } else if (componentInstance instanceof ShelfContextMenuComponent) {
              this.handleShelfMenuAction(
                action,
                componentInstance.params.dropIndex,
                componentInstance.params.shelfIndex
              );
            } else if (componentInstance instanceof ProductContextMenuComponent) {
              this.handleProductMenuAction(
                action,
                componentInstance.params.dropIndex,
                componentInstance.params.shelfIndex,
                componentInstance.params.stackIndex,
                componentInstance.params.productIndex
              );
            } else if (componentInstance instanceof ProductBinContextMenuComponent) {
              this.handleProductBinMenuAction(action, componentInstance.params.productId);
            }
            emitter.emit(action);
            this.closeContextMenu();
          }
        });
      });
  }

  private createMouseClickSub(menuElement: HTMLElement): Subscription {
    return fromEvent<MouseEvent>(document, 'click').subscribe((e) => {
      if (e.button === 0 && !menuElement.contains(e.target as Node)) {
        this.closeContextMenu();
      }
    });
  }

  private createMouseMoveSub(menuElement: HTMLElement): Subscription {
    return fromEvent<MouseEvent>(document, 'mousemove').subscribe((e) => {
      const menuRect = menuElement.getBoundingClientRect();
      const buffer = 100;

      const isMouseOverMenu =
        menuElement.contains(e.target as Node) || e.target === menuElement;

      if (isMouseOverMenu) {
        return;
      }

      const xInRange =
        e.clientX >= menuRect.left - buffer &&
        e.clientX <= menuRect.right + buffer;
      const yInRange =
        e.clientY >= menuRect.top - buffer &&
        e.clientY <= menuRect.bottom + buffer;

      if (!xInRange || !yInRange) {
        this.closeContextMenu();
      }
    });
  }
}
